<template>
  <b-row class="mt-0">
    <b-col
      :class="{ 'not-valid': !isValid }"
      cols="12"
      @change="somethingChanged()"
    >
      <general-section
        @updateCampaignNameValid="updateCampaignNameValid"
        @updateAdvertiser="updateAdvertiser"
        @updateAgency="updateAgency"
        @updateCurrency="updateCurrency"
        @updateCampaignInfo="updateCampaignInfo"
      />
      <div class="mb-5">
        <base-help-tooltip>
          <template slot="left">
            <h3>
              Budget & Schedule
            </h3>
          </template>
          <template slot="popover-body">
            <p>
              <b>Campaign Period</b>
              The dates your campaign will run. Select the start and end dates for your campaign. Once it's approved, your campaign will automatically begin delivering impressions on the start date until midnight on the end date.
            </p>
            <p>
              <b>Campaign Budget (excl. VAT)</b>
              Enter the total budget for the campaign period, excluding VAT. This is the maximum amount the campaign will spend over the campaign period.
              <br>
              Example: If you plan to spend £100 per day for 7 days, enter £700 as your total budget.
            </p>
            <p>
              <b>Note:</b> VAT will be added for UK-registered companies.
            </p>
          </template>
        </base-help-tooltip>
        <div
          v-if="GET_IS_MANAGED_CAMPAIGN"
          class="mb-3"
        >
          <b-row>
            <b-col
              lg="5"
              xl="4"
            >
              <div
                class="d-flex align-items-center mt-5"
              >
                <b-form-checkbox
                  id="add-impression-target-checkbox"
                  name="add-impression-target-checkbox"
                  v-model="isAddImpressionTargetChecked"
                  @change="toggleAddImpressionTarget"
                  size="xl"
                  class="add-impression-target-checkbox"
                  data-test="add-impression-target-checkbox"
                  :disabled="isAddImpressionTargetCheckboxDisabled"
                />
                <div
                  class="text-inherit"
                  data-test="add-impression-target-name"
                >
                  Add impression target
                </div>
              </div>
            </b-col>
          </b-row>
        </div>
        <div
          v-for="(period, index) in newCampaign.periods"
          :key="index + campaignPeriodControlsKey"
          class="mb-1"
        >
          <campaign-period-controls
            :class="{'mb-2': (index !== newCampaign.periods.length - 1)}"
            :ref="`campaign-period-controls-${index}`"
            :key="index"
            :periods="newCampaign.periods"
            :period-index="index"
            :period="period"
            :is-add-impression-target-checked="newCampaign.isAddImpressionTargetChecked"
            @updatePeriod="updatePeriods(index, ...arguments)"
            @deletePeriod="deletePeriod(index)"
            @updatePeriodsValidation="updatePeriodsValidations(index, ...arguments)"
          />
          <b-row
            v-if="isDormantPeriod(index)"
            class="gutters-0 my-3"
          >
            <b-col cols="auto">
              <p
                cols="auto"
                class="alert alert-sm alert-info mb-0"
              >
                Dormant Period {{ getDormantPeriod(period.endDate, newCampaign.periods[index + 1].startDate) }}
              </p>
            </b-col>
          </b-row>
        </div>
        <b-row class="gutters-0">
          <b-col cols="auto">
            <p
              :class="['mb-0 mt-2 ', [isAddPeriodDisabled ? 'cursor-not-allowed' : 'cursor-pointer' ]]"
              @click="addPeriod"
              :disabled="isAddPeriodDisabled"
            >
              Add additional campaign period <span class="ml-1"><i class="fal fa-plus" /></span>
            </p>
          </b-col>
        </b-row>
      </div>
      <div class="mb-5">
        <base-help-tooltip>
          <template slot="left">
            <h3
              class=""
              data-test="targeting-label"
            >
              Targeting
            </h3>
          </template>
          <template slot="popover-body">
            <p>
              The countries or regions in which you wish to target your advert.
              Select countries one by one using the green tick icon or a whole
              region and exclude individual countries using the red 'X' icon.
              Collapse or expand a region using the &#8743; or &#8744; and use
              'select all' or 'deselect all' to control the inclusion of all countries in a region.
            </p>
          </template>
        </base-help-tooltip>
        <b-row class="mb-5">
          <b-col>
            <b-form-group
              id="fieldset-7"
              label="Countries"
              label-for="input-7"
              label-class="form-label"
            >
              <template
                slot="label"
              >
                Countries <span class="form-required">*</span>
              </template>

              <region-countries-list
                :targeting="geoTargeting"
                ref="regionCountriesList"
                @addCountry="addCountry"
                @addRegion="addRegion"
                @removeRegion="removeRegion"
                :read-only="GET_IS_CAMPAIGN_READONLY"
              />
            </b-form-group>
            <div
              class="tags"
              data-test="geo-targeting-tags"
            >
              <template v-if="hasRegionTargeting">
                <h6>Region</h6>
                <span
                  v-for="(id) in geoTargeting.regions"
                  :key="`region-${id}`"
                  class="tag tag-primary"
                >
                  {{ getRegionName(id) }}
                  <a
                    href="#"
                    class="tag-addon"
                    @click.prevent="removeRegion(id)"
                  >
                    <i class="fa-light fa-xmark" />
                  </a>
                </span>
              </template>
              <template v-if="geoTargeting.countryInclusionsExclusions.inclusions.length > 0">
                <h6 class="mt-3">
                  Include
                </h6>
                <span
                  v-for="(item, index) in geoTargeting.countryInclusionsExclusions.inclusions"
                  :key="`include-${index}`"
                  class="tag tag-success include"
                >
                  {{ getCountyName(item) }}
                  <a
                    href="#"
                    class="tag-addon"
                    @click.prevent="deleteTargeting('inclusions',item)"
                    :disabled="GET_IS_CAMPAIGN_READONLY"
                  >
                    <i class="fa-light fa-xmark" />
                  </a>
                </span>
              </template>
              <template v-if="geoTargeting.countryInclusionsExclusions.exclusions.length > 0">
                <h6 class="mt-3">
                  Exclude
                </h6>
                <span
                  v-for="(item, index) in geoTargeting.countryInclusionsExclusions.exclusions"
                  :key="`exclude-${index}`"
                  class="tag tag-danger exclude"
                >
                  {{ getCountyName(item) }}
                  <a
                    href="#"
                    class="tag-addon"
                    @click.prevent="deleteTargeting('exclusions', item)"
                    :disabled="GET_IS_CAMPAIGN_READONLY"
                  >
                    <i class="fa-light fa-xmark" />
                  </a>
                </span>
              </template>
            </div>
          </b-col>
        </b-row>
      </div>
      <div class="mb-5">
        <base-help-tooltip>
          <template slot="left">
            <h3
              class=""
              data-test="brand-safety-label"
            >
              Brand Safety Keywords
            </h3>
          </template>
          <template slot="popover-body">
            <p>
              We will apply our industry brand protection using Natural Language Processing
              to prevent your ads appearing against content in any of the following categories:
            </p>
            <p class="bg-light p-3 rounded">
              abortion, air-crash, banking-economic-and-financial-crime, child-mistreatment,
              crime, cyber-threat, danger-and-death, demonstrations,
              diseases-and-syndromes, drug-addiction, drunk-and-disorderly,
              extreme-right-and-nationalist-parties, financial-crisis, fire, fraud,
              industrial-accidents, job-loss,
              manipulation-of-people, natural-disasters, prejudicial-discrimination,
              sexual, terrorism, torture,
              unrest-conflicts-and-war
            </p>
            <p>
              Words separated by a space are treated as a phrase (3 consecutive words maximum).
              Use a comma (,) to separate individual keywords or word pairs.
              Words such as ‘and’, ‘but’ or ‘if’ are prohibited from being added.
            </p>
            <p>
              You may supplement our brand protection by excluding up to 20 words or word pairs.
              So for example, you might add green,
              in which case if the word green appears in the content of an article, your ad will
              never be appear there. Alternatively, you might want to be more specific and choose
              'green frog' to only exclude stories which include the consecutive words green and frog.
            </p>
          </template>
        </base-help-tooltip>
        <b-row>
          <b-col
            lg="12"
            xl="8"
          >
            <b-alert
              :show="phrasesWithMoreThanThreeWordsLength > 0"
              variant="sm alert-warning"
              data-test="phrases-with-more-than-three-words-alert"
            >
              Phrases can't exceed more than three words
            </b-alert>
            <b-alert
              :show="bannedWordsLength > 0"
              variant="sm alert-warning"
              data-test="banned-words-alert"
            >
              <div v-if="bannedWordsLength === 1">
                The word <span class="text-danger">{{ bannedWords[0] }}</span> is banned
              </div>
              <div v-else>
                The words <span class="text-danger">{{ bannedWords.join(", ") }}</span> are banned
              </div>
            </b-alert>
          </b-col>
        </b-row>
        <b-row>
          <b-col
            lg="10"
            xl="8"
          >
            <b-form-group
              id="fieldset-1"
              label-for="input-1"
            >
              <b-input-group>
                <b-form-input
                  id="input-6"
                  v-model="keyword"
                  autocomplete="off"
                  trim
                  @paste.prevent="onPasteKeyword"
                  @keyup.enter.native="addKeyword"
                  :formatter="formatBrandSafetyKeywords"
                  data-test="campaign-keyword"
                  :disabled="GET_IS_CAMPAIGN_READONLY"
                  :readonly="GET_IS_CAMPAIGN_READONLY"
                />
                <b-input-group-append>
                  <b-button
                    variant="option"
                    :disabled="disabledAddKeyword"
                    @click="addKeyword"
                    data-test="campaign-add-keyword"
                  >
                    Exclude
                  </b-button>
                </b-input-group-append>
              </b-input-group>
            </b-form-group>
          </b-col>
          <b-col>
            <span
              class="badge badge-default"
              :class="{ 'bg-light': keywords.length === maxKeywords }"
            >
              {{ keywords.length }} / {{ maxKeywords }}
            </span>
          </b-col>
          <b-col cols="12">
            <div
              class="tags mt-2 mb-5"
              data-test="keyword-tags"
              v-if="keywords.length > 0"
            >
              <span
                class="tag tag-primary"
                v-for="(item, key) in keywords"
                :key="key"
                :class="{ 'tag-danger': checkKeyword(item) }"
                :id="`tooltip-target-${key}`"
              >
                {{ item }}
                <b-tooltip
                  :target="`tooltip-target-${key}`"
                  triggers="hover"
                  :disabled="!checkKeyword(item)"
                >
                  "{{ item }}" contains a word that is banned
                </b-tooltip>
                <a
                  href="javascript:;"
                  class="tag-addon"
                  @click="onDeleteKeyword(key)"
                  :disabled="GET_IS_CAMPAIGN_READONLY"
                >
                  <i class="fa-light fa-xmark" />
                </a>
              </span>
            </div>
          </b-col>
        </b-row>
      </div>
      <div>
        <base-help-tooltip>
          <template slot="left">
            <h3
              class=""
              data-test="targeting-label"
            >
              Publisher Verticals
            </h3>
          </template>
          <template slot="popover-body">
            <p>
              The list of publishers your advert will appear on during the campaign. Please select as many verticals as you wish (at least one vertical must be selected to proceed).
              Please only select the ‘French’ or ‘German’ verticals if your adverts are in either of those languages.
            </p>
          </template>
        </base-help-tooltip>
        <custom-publishers
          ref="customPublishersSelector"
          :order-set="this.order"
          :disabled="GET_IS_CAMPAIGN_READONLY"
          @updatePublishersCreativesState="updatePublishersCreativesState"
          @removePublisherFromPublishersCreativesList="removePublisherFromPublishersCreativesList"
        />
      </div>
    </b-col>
  </b-row>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import * as campaignTypes from '@/modules/campaigns/store/types';
import suppressibleBox from '@/core/mixins/suppressibleBox';
import currenciesMixin from '@/core/mixins/currencies';

import _ from 'lodash';
import helper from '@/core/helper';
import dateHelper from '@/core/mixins/dateHelper';
import CampaignPeriodControls from '@/modules/campaigns/components/CampaignInfo/CampaignPeriodControls';
import RegionCountriesList from '@/modules/campaigns/components/RegionCountriesList';
import CustomPublishers from '@/modules/campaigns/components/CustomPublishers';
import GeneralSection from '@/modules/campaigns/components/GeneralSection';
import BaseHelpTooltip from '@/components/BaseHelpTooltip.vue';

import {
  parseISO, addDays, subDays, differenceInDays, isPast,
} from 'date-fns';

export default {
  name: 'CampaignInfo',
  mixins: [
    currenciesMixin,
    suppressibleBox,
    dateHelper,
  ],
  components: {
    BaseHelpTooltip,
    CampaignPeriodControls,
    RegionCountriesList,
    CustomPublishers,
    GeneralSection,
  },
  data() {
    return {
      newCampaign: {
        name: '',
        currencyId: 2,
        budget: null,
        start: null,
        end: null,
        periods: [
          {
            budget: null,
            impressionTarget: null,
            startDate: null,
            endDate: null,
          },
        ],
        type: null,
        accountManagerUserId: null,
        salesUserId: null,
        isAddImpressionTargetChecked: false,
      },
      newPeriodsValidations: {},
      options: [],
      isLoading: false,
      keyword: '',
      keywords: [],
      bannedWords: [],
      phrasesWithMoreThanThreeWords: [],
      maxBrandSafetyPhraseLength: 255,
      maxKeywords: 999,
      geoTargeting: {
        regions: [],
        countryInclusionsExclusions: {
          inclusions: [],
          exclusions: [],
        },
      },
      publishersGroups: {},
      selectedAdvertiser: {
        name: '',
      },
      selectedAgency: {},
      campaignNameValid: null,
      updatedPublishersCreatives: [],
      campaignPeriodControlsKey: 0,
    };
  },
  computed: {
    ...mapState('campaigns', [
      'campaign',
      'order',
      'linkingWords',
      'countries',
      'regions',
      'blacklist',
      'publisherIds',
      'selectedPublisherGroups',
    ]),
    ...mapState('content', [
      'creatives',
    ]),
    ...mapState('account', {
      accountId: 'id',
    }),
    ...mapGetters('campaigns', [
      campaignTypes.GET_IS_NEW_CAMPAIGN,
      campaignTypes.GET_IS_MANAGED_CAMPAIGN,
      campaignTypes.GET_IS_VIEW_TYPE,
      campaignTypes.GET_IS_CAMPAIGN_READONLY,
      campaignTypes.GET_CAMPAIGN_TOTAL_BUDGET,
      campaignTypes.GET_CAMPAIGN_MIN_START_DATE,
      campaignTypes.GET_CAMPAIGN_MAX_END_DATE,
    ]),
    isAddImpressionTargetCheckboxDisabled() {
      if (this.GET_IS_CAMPAIGN_READONLY) return true;
      if (!this.isAddImpressionTargetChecked) return false;

      return this.newCampaign.periods
        .filter((period) => period.impressionTarget !== null)
        .filter((period) => this.hasPeriodEnded(period.startDate, period.endDate) > 0)
        .length > 0;
    },
    arePeriodsValid() {
      for (const period of this.newCampaign.periods) {
        for (const property in period) {
          if (property !== 'impressionTarget' && period[property] == null) return false;
        }
      }
      return !Object.values(this.newPeriodsValidations).includes(false);
    },
    isAddPeriodDisabled() {
      return this.GET_IS_CAMPAIGN_READONLY
      || (this.newCampaign.periods.length
          && !this.hasPeriodRequiredFields(this.newCampaign.periods[this.newCampaign.periods.length - 1]));
    },
    isValid() {
      if (this.isLoading) return false;
      const validations = [];
      const isValidGeoTargeting = this.isGeoTargetingPresent
      && this.areAllCountriesExcludedInRegion;
      const isValidBrandSafety = this.checkKeywordsValid()
        && this.bannedWordsLength === 0 && this.phrasesWithMoreThanThreeWordsLength === 0;
      validations.push(this.newCampaign.name !== '' && this.campaignNameValid !== false);
      validations.push(this.arePeriodsValid);
      validations.push(isValidGeoTargeting);
      validations.push(isValidBrandSafety);
      validations.push(this.publisherIds.length !== 0);
      if (this.GET_IS_MANAGED_CAMPAIGN) {
        validations.push(!!this.newCampaign.accountManagerUserId);
        validations.push(!!this.newCampaign.salesUserId);
      }
      const areAllValid = validations.every((validation) => validation === true);
      this.$emit('setNotValidForNavigation', 'last', !areAllValid);
      this.$emit('setNotValidForNavigation', 'next', !areAllValid);
      return areAllValid;
    },
    isGeoTargetingPresent() {
      return this.geoTargeting.regions.length > 0
      || this.geoTargeting.countryInclusionsExclusions.inclusions.length > 0;
    },
    bannedWordsLength() {
      return this.bannedWords.length;
    },
    phrasesWithMoreThanThreeWordsLength() {
      return this.phrasesWithMoreThanThreeWords.length;
    },
    hasIncludeTargeting() {
      return (this.geoTargeting.countryInclusionsExclusions.inclusions.length !== 0);
    },
    hasExcludeTargeting() {
      return (this.geoTargeting.countryInclusionsExclusions.exclusions.length !== 0);
    },
    hasRegionTargeting() {
      return this.geoTargeting.regions.length > 0;
    },
    disabledAddKeyword() {
      return this.bannedWordsLength > 0
      || this.phrasesWithMoreThanThreeWordsLength > 0
      || this.keywords.length === this.maxKeywords
      || this.keyword.length === 0;
    },
    areAllCountriesExcludedInRegion() {
      if (this.isLoading) return true;
      if (this.geoTargeting.regions.length === 0
      || this.geoTargeting.countryInclusionsExclusions.exclusions.length === 0
      || this.regions.length === 0) return true;
      const regionValid = this.geoTargeting.regions
        .map((region) => {
          const refRegion = this.regions.find((i) => i.id === region);
          const refRegionCountriesLength = refRegion.countries.length;
          const exclusionLength = this.geoTargeting.countryInclusionsExclusions.exclusions
            .filter((country) => refRegion.countries.includes(country)).length;
          return (refRegionCountriesLength !== exclusionLength);
        });
      return !regionValid.includes(false);
    },
    currentOrderDetails() {
      return {
        startDate: this.newCampaign.start,
        endDate: this.newCampaign.end,
        budget: this.newCampaign.budget,
        periods: this.newCampaign.periods,
        currencyId: this.newCampaign.currencyId,
        geoTargeting: this.geoTargeting,
        publisherIds: this.publisherIds,
      };
    },
    publisherGroupName() {
      return this.GET_IS_MANAGED_CAMPAIGN ? 'MANAGED_SECTOR' : 'SECTOR';
    },
    hasPeriodsChanged() {
      if (!this.order.hasOwnProperty('periods')) return false;
      const isEqualLength = this.newCampaign.periods.length === this.order.periods.length;
      if (isEqualLength) {
        const isEqualValue = (value1, value2) => (value1 === value2);
        for (let index = 0; index < this.newCampaign.periods.length; index++) {
          if (!isEqualValue(this.newCampaign.periods[index].budget, this.order.periods[index].budget)
              || !isEqualValue(this.newCampaign.periods[index].startDate, this.order.periods[index].startDate)
              || !isEqualValue(this.newCampaign.periods[index].endDate, this.order.periods[index].endDate)) {
            return true;
          }
        }
      }
      return true;
    },
    isAddImpressionTargetChecked: {
      get() {
        if (!this.order.periods) return false;
        const doPeriodsContainImpressionTarget = this.order.periods.some((period) => period.impressionTarget);
        this.updateIsAddImpressionTargetChecked(doPeriodsContainImpressionTarget);
        return doPeriodsContainImpressionTarget;
      },
      set(val) {
        this.value = val;
      },
    },
  },
  methods: {
    ...mapActions('campaigns', [
      campaignTypes.CREATE_CAMPAIGN,
      campaignTypes.PATCH_CAMPAIGN,
      campaignTypes.DELETE_CAMPAIGN,
      campaignTypes.DELETE_FEED,
      campaignTypes.CREATE_ORDER,
      campaignTypes.DELETE_ORDER,
      campaignTypes.PATCH_ORDER,
      campaignTypes.FETCH_COUNTRIES,
      campaignTypes.PATCH_BLACKLIST,
      campaignTypes.FETCH_PUBLISHERS_GROUPS,
      campaignTypes.FETCH_EXISTING_CONTENT_PROVIDER,
      campaignTypes.CREATE_DEAL_OVERRIDES,
      campaignTypes.CREATE_AGENCY,
      campaignTypes.UPDATE_DEAL_OVERRIDES,
      campaignTypes.GET_CAMPAIGN_BY_NAME,
      campaignTypes.UPDATE_PUBLISHERS_CREATIVES,
      campaignTypes.SET_CAMPAIGN_INFO_CHANGES,
      campaignTypes.CREATE_ADVERTISER,
    ]),
    updateIsAddImpressionTargetChecked(doPeriodsContainImpressionTarget) {
      this.newCampaign.isAddImpressionTargetChecked = doPeriodsContainImpressionTarget;
    },
    onSubmit(isDirty) {
      if (!this.isValid) return Promise.reject();
      if (!isDirty) return Promise.resolve();
      if (this.keyword !== '') this.addKeyword();

      if (typeof this.campaign.name !== 'undefined') return this.updateCampaign();
      return this.createCampaign();
    },
    createCampaign() {
      return new Promise(async (resolve, reject) => {
        if (this.isLoading) return;
        this.isLoading = true;
        try {
          if (this.selectedAdvertiser.id === -1) {
            await this.CREATE_ADVERTISER(this.selectedAdvertiser.name)
              .then((advertiser) => {
                this.selectedAdvertiser = advertiser;
              });
          }
          if (this.selectedAgency.id === -1) {
            await this.CREATE_AGENCY(this.selectedAgency.name)
              .then((agency) => {
                this.selectedAgency = agency;
              });
          }
          await this.FETCH_EXISTING_CONTENT_PROVIDER();

          const {
            name, accountManagerUserId, salesUserId, group,
          } = this.newCampaign;
          let campaignPayload = {
            name,
            accountManagerUserId,
            salesUserId,
            group,
          };
          if (this.GET_IS_MANAGED_CAMPAIGN) {
            campaignPayload = {
              ...campaignPayload,
              agencyId: this.selectedAgency.id,
            };
          }
          const createCampaign = await this.CREATE_CAMPAIGN(campaignPayload);
          const orderPayload = {
            advertiserId: createCampaign.advertiserId,
            currencyId: this.newCampaign.currencyId,
            budget: this.GET_CAMPAIGN_TOTAL_BUDGET,
            startDate: this.GET_CAMPAIGN_MIN_START_DATE,
            endDate: this.GET_CAMPAIGN_MAX_END_DATE,
            periods: this.newCampaign.periods,
            geoTargeting: this.geoTargeting,
            publisherIds: this.publisherIds,
          };
          if (this.selectedPublisherGroups.length !== 0) {
            orderPayload.publisherGroupIds = this.publishersGroups
              .filter((publisherGroup) => this.selectedPublisherGroups.includes(publisherGroup.name))
              .map((publisher) => publisher.id);
          }
          const createOrder = await this.CREATE_ORDER(orderPayload);
          Promise.all([
            createCampaign,
            createOrder,
          ]).then(async () => {
            await this.PATCH_BLACKLIST(this.keywords);
            this.isLoading = false;
            this.$emit('setIsDirty', false);
            resolve();
          });
        } catch (error) {
          this.handleError(error);
          reject(error);
        } finally {
          this.newPeriodsValidations = {};
          this.SET_CAMPAIGN_INFO_CHANGES({});
        }
      });
    },
    updateCampaign() {
      return new Promise(async (resolve, reject) => {
        this.isLoading = true;
        try {
          if (this.selectedAgency.id === -1) {
            await this.CREATE_AGENCY(this.selectedAgency.name)
              .then((agency) => {
                this.selectedAgency = agency;
              });
          }
          const {
            name, accountManagerUserId, salesUserId, group,
          } = this.newCampaign;
          let campaignPayload = {
            name,
            accountManagerUserId,
            salesUserId,
            group,
          };
          let orderPayload = {
            geoTargeting: this.geoTargeting,
          };
          if (!this.GET_IS_CAMPAIGN_READONLY) {
            const {
              currencyId, start, end,
            } = this.newCampaign;
            orderPayload = {
              ...orderPayload,
              currencyId,
              budget: this.GET_CAMPAIGN_TOTAL_BUDGET,
              startDate: helper.convertDateToString(start),
              endDate: helper.convertDateToString(end),
            };
          }
          if (this.GET_IS_MANAGED_CAMPAIGN) {
            campaignPayload = {
              ...campaignPayload,
              agencyId: this.selectedAgency.id,
            };
          }
          if (this.hasPeriodsChanged) {
            orderPayload = {
              ...orderPayload,
              startDate: this.GET_CAMPAIGN_MIN_START_DATE,
              endDate: this.GET_CAMPAIGN_MAX_END_DATE,
              budget: this.GET_CAMPAIGN_TOTAL_BUDGET,
              periods: this.newCampaign.periods,
            };
          }
          if (this.selectedPublisherGroups.length !== 0) {
            orderPayload.publisherGroupIds = this.publishersGroups
              .filter((publisherGroup) => this.selectedPublisherGroups.includes(publisherGroup.name))
              .map((item) => item.id);
          }
          orderPayload.publisherIds = this.publisherIds;

          const patchCampaign = await this.PATCH_CAMPAIGN(campaignPayload);
          const patchOrder = await this.PATCH_ORDER(orderPayload);
          const patchBlacklist = await this.PATCH_BLACKLIST(this.keywords);
          Promise.all([
            patchCampaign,
            patchOrder,
            patchBlacklist,
          ]).then(async (response) => {
            if (this.updatedPublishersCreatives.length) {
              this.patchPublishersCreatives(response[0].advertiserId, response[0].id);
            }
            this.$emit('setIsDirty', false);
            resolve();
          });
        } catch (error) {
          reject(error);
        } finally {
          this.isLoading = false;
          this.newPeriodsValidations = {};
          this.SET_CAMPAIGN_INFO_CHANGES({});
        }
      });
    },
    addKeyword() {
      if (this.keywords.length === this.maxKeywords) return;
      if (this.keyword.length === 0) return;
      let list = this.formatBrandSafetyKeywords(this.keyword);
      list = list.split(',');
      for (let i = 0; i < list.length; i++) {
        if (this.keywords.indexOf(list[i]) === -1
        && !this.hasBannedKeywords(list[i]) && !this.hasMoreThanThreeKeywords(list[i])) {
          this.keywords.push(list[i].trim());
        }
        if (this.keywords.length >= this.maxKeywords) break;
      }
      this.keyword = '';
    },
    onPasteKeyword(event) {
      if (this.keywords.length === this.maxKeywords) return;
      let list = event.clipboardData.getData('text')
        .split(/\r?\n/)
        .filter((el) => el !== '');
      if (list.length === 1) {
        list = this.formatBrandSafetyKeywords(list[0]);
        list = list.split(',');
      }
      if (list.length === 0) return;
      const pasteList = [];
      for (let i = 0; i < list.length; i++) {
        let phrase = list[i].trim();
        if (this.keywords.length + i === this.maxKeywords) break;
        if (!this.keywords.includes(phrase)) {
          if (!this.hasBannedKeywords(phrase) && !this.hasMoreThanThreeKeywords(phrase)) {
            if (phrase.length > this.maxBrandSafetyPhraseLength) {
              phrase = phrase.substring(0, this.maxBrandSafetyPhraseLength);
            }
            if (phrase !== '') pasteList.push(phrase);
          }
        }
      }
      this.keywords = [...this.keywords, ...pasteList];
      this.keyword = '';
      this.somethingChanged();
    },
    checkKeyword(word) {
      return this.hasBannedKeywords(word);
    },
    onDeleteKeyword(index) {
      if (!this.GET_IS_CAMPAIGN_READONLY) {
        this.keywords.splice(index, 1);
        this.somethingChanged();
      }
    },
    checkKeywordsValid() {
      if (this.keywords.length > this.maxKeywords) return false;
      let pass = true;
      let i;
      const l = this.keywords.length;
      for (i = 0; i < l; i += 1) {
        // eslint-disable-next-line no-continue
        if (!this.checkKeyword(this.keywords[i])) continue;
        pass = false;
        break;
      }
      return pass;
    },
    getCountyName(name) {
      return this.countries[name];
    },
    getRegionName(id) {
      if (!this.regions.find((i) => i.id === id)) return 'Name not found';
      return this.regions.find((i) => i.id === id).name;
    },
    deleteTargeting(listName, item) {
      if (!this.GET_IS_CAMPAIGN_READONLY) {
        const index = this.geoTargeting.countryInclusionsExclusions[listName]
          .findIndex((i) => i === item);
        if (index !== -1) this.geoTargeting.countryInclusionsExclusions[listName].splice(index, 1);
        this.$refs.regionCountriesList.setTargeting();
        this.somethingChanged();
      }
    },
    addCountry(label, country, regionId) {
      const oppositeList = (label === 'include') ? 'exclusions' : 'inclusions';
      const oppositeListIndex = this.geoTargeting.countryInclusionsExclusions[oppositeList]
        .findIndex((i) => i === country);
      if (oppositeListIndex !== -1) {
        this.geoTargeting.countryInclusionsExclusions[oppositeList]
          .splice(oppositeListIndex, 1);
      }
      const list = (label === 'include') ? 'inclusions' : 'exclusions';
      const index = this.geoTargeting.countryInclusionsExclusions[list]
        .findIndex((i) => i === country);
      if (list === 'exclusions' || (list === 'inclusions' && !this.geoTargeting.regions.some((i) => i === regionId))) {
        if (index === -1) this.geoTargeting.countryInclusionsExclusions[list].push(country);
      }
      this.somethingChanged();
    },
    addRegion(id) {
      this.geoTargeting.countryInclusionsExclusions
        .inclusions = this.geoTargeting.countryInclusionsExclusions.inclusions
          .filter((country) => !this.regions.find((i) => i.id === id).countries.includes(country));

      const index = this.geoTargeting.regions.some((i) => i === id);
      if (index !== -1) this.geoTargeting.regions.push(id);
      this.$refs.regionCountriesList.setTargeting();
      this.somethingChanged();
    },
    removeRegion(id) {
      if (!this.GET_IS_CAMPAIGN_READONLY) {
        const index = this.geoTargeting.regions.findIndex((i) => i === id);
        if (index !== -1) this.geoTargeting.regions.splice(index, 1);
        this.$refs.regionCountriesList.setTargeting();
        this.somethingChanged();
      }
    },
    async populatePageForExistingCampaign() {
      ['name', 'type', 'accountManagerUserId', 'salesUserId'].forEach((key) => {
        if (this.campaign[key]) this.newCampaign[key] = this.campaign[key];
      });
    },
    updateCurrency(id) {
      this.newCampaign.currencyId = id;
      this.somethingChanged();
    },
    resetPeriods() {
      this.$set(this.newCampaign, 'periods', this.assignPeriods(this.order));
      this.campaignPeriodControlsKey++;

      this.SET_CAMPAIGN_INFO_CHANGES({
        target: 'order',
        property: 'periods',
        value: [...this.newCampaign.periods],
      });
    },
    assignPeriods(order) {
      if (order.periods?.length) return _.cloneDeep(order.periods);
      return [{
        budget: order.budget ?? null,
        startDate: order.startDate ?? null,
        endDate: order.endDate ?? null,
      }];
    },
    updatePeriods(index, newPeriod) {
      this.newCampaign.periods.splice(index, 1, newPeriod);
      if (this.hasPeriodsChanged || this.GET_IS_NEW_CAMPAIGN) {
        this.SET_CAMPAIGN_INFO_CHANGES({
          target: 'order',
          property: 'periods',
          value: [...this.newCampaign.periods],
        });
      } else {
        this.SET_CAMPAIGN_INFO_CHANGES({
          target: 'order',
          property: 'periods',
          value: null,
        });
      }
      this.somethingChanged();
    },
    hasPeriodRequiredFields(period) {
      return !!period.startDate
          && !!period.endDate
          && !!period.budget;
    },
    addPeriod() {
      if (this.isAddPeriodDisabled) return;
      this.newCampaign.periods.push({
        budget: null,
        startDate: null,
        endDate: null,
      });
      this.$set(this.newPeriodsValidations, `${this.newCampaign.periods.length - 1}`, false);
    },
    deletePeriod(index) {
      this.newCampaign.periods.splice(index, 1);
      delete this.newPeriodsValidations[index];
      this.SET_CAMPAIGN_INFO_CHANGES({
        target: 'order',
        property: 'periods',
        value: [...this.newCampaign.periods],
      });
      this.campaignPeriodControlsKey++;
      if (this.hasPeriodsChanged) this.somethingChanged();
    },
    isDormantPeriod(index) {
      if (index === this.newCampaign.periods.length - 1 || this.newCampaign.periods.length < 2) return false;
      const { endDate } = this.newCampaign.periods[index];
      const { startDate } = this.newCampaign.periods[index + 1];
      return differenceInDays(parseISO(startDate), parseISO(endDate)) > 1;
    },
    getDormantPeriod(endDate, startDate) {
      return `${this.formatDateForPlatform(addDays(new Date(endDate), 1))} - ${this.formatDateForPlatform(subDays(new Date(startDate), 1))}`;
    },
    updatePeriodsValidations(index, isValid) {
      if (this.newPeriodsValidations[index]) {
        this.newPeriodsValidations[index] = isValid;
        return;
      }
      this.$set(this.newPeriodsValidations, `${index}`, isValid);
    },
    populatePageForExistingOrder() {
      this.newCampaign.periods = this.assignPeriods(this.order);
      if (this.GET_IS_NEW_CAMPAIGN) return;
      this.newCampaign.budget = this.order.budget ?? null;
      this.newCampaign.currencyId = this.order.currencyId ?? 2;
      this.newCampaign.start = this.order.startDate ? helper.parseISOString(this.order.startDate) : null;
      this.newCampaign.end = this.order.endDate ? helper.parseISOString(this.order.endDate) : null;
      if (typeof this.order.geoTargeting !== 'undefined') {
        this.geoTargeting = this.order.geoTargeting;
      }
    },
    populatePageForExistingBlacklist() {
      this.keywords = this.blacklist.blacklistKeywords ?? [];
    },
    hasBannedKeywords(phrase) {
      const isAllStopwords = [];
      const keywords = phrase.trim().split(' ');

      keywords.forEach((keyword) => {
        if (this.linkingWords.includes(keyword)) {
          if (!this.bannedWords.includes(keyword)) {
            isAllStopwords.push(true);
          }
        } else {
          isAllStopwords.push(false);
        }
      });
      const hasBannedKeyword = isAllStopwords.every(Boolean);
      if (hasBannedKeyword) {
        isAllStopwords.forEach((value, index) => {
          if (value) this.bannedWords.push(keywords[index]);
        });
      }
      return hasBannedKeyword;
    },
    hasMoreThanThreeKeywords(phrase) {
      const keywords = phrase.trim().split(/\s+/);
      if (keywords.length > 3) {
        if (!this.phrasesWithMoreThanThreeWords.includes(phrase)) {
          this.phrasesWithMoreThanThreeWords.push(phrase);
        }
        return true;
      }
      return false;
    },
    populatePageForExistingEntities() {
      this.FETCH_PUBLISHERS_GROUPS(this.publisherGroupName)
        .then((groups) => {
          this.publishersGroups = groups;
        });
      this.$nextTick(async () => {
        Promise.all([
          await this.populatePageForExistingCampaign(),
          await this.populatePageForExistingOrder(),
          await this.populatePageForExistingBlacklist(),
        ]).finally(() => {
          this.isLoading = false;
        });
      });
    },
    toggleAddImpressionTarget(isChecked) {
      this.newCampaign.isAddImpressionTargetChecked = isChecked;
      if (!isChecked) {
        for (let i = 0; i < this.newCampaign.periods.length; i++) {
          this.newCampaign.periods[i].impressionTarget = null;
          this.$refs[`campaign-period-controls-${i}`][0].updateImpressionTarget(null);
        }
      }
    },
    formatBrandSafetyKeywords(value) {
      const phrases = value.split(',');
      const trimmedPhrases = [];
      phrases.forEach((phrase) => {
        if (phrase.length > this.maxBrandSafetyPhraseLength) {
          trimmedPhrases.push(phrase.substring(0, this.maxBrandSafetyPhraseLength));
        } else {
          trimmedPhrases.push(phrase);
        }
      });
      return [...new Set(trimmedPhrases)].join(',');
    },
    updateOrderDetails() {
      this.$emit('updateOrderDetails', this.currentOrderDetails);
    },
    handleError() {
      this.$nextTick(async () => {
        await this.DELETE_ORDER();
        await this.DELETE_CAMPAIGN();
        this.isLoading = false;
      });
    },
    somethingChanged() {
      this.$emit('setIsDirty', true);
    },
    async updateAdvertiser(advertiser) {
      this.selectedAdvertiser = advertiser;
      if (this.GET_IS_NEW_CAMPAIGN) {
        this.somethingChanged();
      }
    },
    updateAgency(agency) {
      this.selectedAgency = agency;
      this.somethingChanged();
    },
    updateCampaignNameValid(isValid) {
      this.campaignNameValid = isValid;
    },
    updateCampaignInfo(property, value) {
      this.newCampaign[property] = value;
      this.somethingChanged();
    },
    updatePublishersCreativesState(updatedItem, defaultPublisherCreative) {
      if (!this.updatedPublishersCreatives.length) return this.updatedPublishersCreatives.push(updatedItem);
      this.updatedPublishersCreatives.forEach((aCreative, index) => {
        const isEqualCreativeId = aCreative.creativeId === updatedItem.creativeId;
        const isEqualPublisherId = aCreative.publisherId === updatedItem.publisherId;
        if (isEqualCreativeId && isEqualPublisherId) this.updatedPublishersCreatives.splice(index, 1);
      });
      if (!defaultPublisherCreative) {
        return updatedItem.isPaused ? this.updatedPublishersCreatives.push(updatedItem) : this.updatedPublishersCreatives;
      }
      const defaultIsPaused = defaultPublisherCreative[0].isPaused;
      this.somethingChanged();
      return defaultIsPaused === updatedItem.isPaused ? this.updatedPublishersCreatives : this.updatedPublishersCreatives.push(updatedItem);
    },
    removePublisherFromPublishersCreativesList(id) {
      this.updatedPublishersCreatives.forEach((aCreative, index) => {
        if (aCreative.publisherId === id) return this.updatedPublishersCreatives.splice(index, 1);
      });
      this.somethingChanged();
    },
    patchPublishersCreatives(advertiserId, campaignId) {
      const updatedCreatives = {
        advertiserId,
        campaignId,
        updatedValues: this.updatedPublishersCreatives,
      };
      const response = this.UPDATE_PUBLISHERS_CREATIVES(updatedCreatives);
      if (response.status === 200) this.updatedPublishersCreatives = [];
    },
    hasPeriodEnded(startDate, endDate) {
      if (!startDate || !endDate) return false;
      return isPast(new Date(endDate)) && isPast(new Date(endDate));
    },
  },
  async mounted() {
    this.newCampaign = {
      name: '',
      budget: null,
      start: null,
      end: null,
      periods: [],
      type: null,
      currencyId: this.currencyId ? this.currencyId : 2,
      accountManagerUserId: null,
      salesUserId: null,
      isAddImpressionTargetChecked: false,
    };
    this.populatePageForExistingEntities();
  },
  watch: {
    campaign() {
      this.populatePageForExistingCampaign();
    },
    order() {
      this.populatePageForExistingOrder();
    },
    blacklist() {
      this.populatePageForExistingBlacklist();
    },
    currentOrderDetails() {
      this.updateOrderDetails();
    },
    keyword() {
      this.bannedWords = [];
      this.phrasesWithMoreThanThreeWords = [];
      const phrases = this.keyword.trim().split(',');
      phrases.forEach((phrase) => {
        this.hasBannedKeywords(phrase);
        this.hasMoreThanThreeKeywords(phrase);
      });
    },
  },
};
</script>
<style lang="scss" scoped>
.tag-addon[disabled] {
  cursor: not-allowed !important;
  opacity: 0.65;
}
</style>
